import type { VenueCategory } from '@/lib/api/apiTypes';
import {
  Beer,
  BottleWine,
  Building2,
  Circle,
  FlaskConical,
  Heart,
  Laugh,
  Lock,
  MapPin,
  Mic,
  Music,
  Music2,
  Sofa,
  Trophy,
  Wind,
  type LucideIcon,
} from 'lucide-react-native';
import {
  getSharedVenueCategoryVisual,
  getSharedVenueMapCategoryVisual,
  resolveSharedVenueCategory,
  VENUE_CATEGORY_VISUALS,
  VENUE_FILTER_CATEGORIES as SHARED_VENUE_FILTER_CATEGORIES,
  VENUE_OWNER_CATEGORIES as SHARED_VENUE_OWNER_CATEGORIES,
  type VenueCategoryIconName,
} from '@radious/catalog/venue-categories';

export interface VenueCategoryConfig {
  label: string;
  icon: LucideIcon;
  color: string;
  bgColor: string;
}

const venueCategoryIcons: Record<VenueCategoryIconName, LucideIcon> = {
  beer: Beer,
  bottleWine: BottleWine,
  building2: Building2,
  circle: Circle,
  flaskConical: FlaskConical,
  heart: Heart,
  laugh: Laugh,
  lock: Lock,
  mapPin: MapPin,
  mic: Mic,
  music: Music,
  music2: Music2,
  sofa: Sofa,
  trophy: Trophy,
  wind: Wind,
};

export const VENUE_CATEGORY_CONFIG = Object.fromEntries(
  Object.keys(VENUE_CATEGORY_VISUALS).map((category) => [
    category,
    withIcon(getSharedVenueCategoryVisual(category)),
  ])
) as Record<VenueCategory, VenueCategoryConfig>;

export const VENUE_OWNER_CATEGORIES = SHARED_VENUE_OWNER_CATEGORIES as ReadonlyArray<{
  value: VenueCategory;
  label: string;
}>;

export const VENUE_FILTER_CATEGORIES = SHARED_VENUE_FILTER_CATEGORIES as ReadonlyArray<{
  value: VenueCategory | '';
  label: string;
}>;

export function resolveVenueCategory(category?: string | null): VenueCategory | null {
  return resolveSharedVenueCategory(category) as VenueCategory | null;
}

export function getVenueCategoryConfig(category?: string | null) {
  const resolvedCategory = resolveVenueCategory(category);
  return resolvedCategory ? VENUE_CATEGORY_CONFIG[resolvedCategory] : VENUE_CATEGORY_CONFIG.other;
}

export function getVenueMapCategoryConfig(category?: string | null): VenueCategoryConfig {
  return withIcon(getSharedVenueMapCategoryVisual(category));
}

function withIcon(spec: {
  bgColor: string;
  color: string;
  iconName: VenueCategoryIconName;
  label: string;
}): VenueCategoryConfig {
  return {
    bgColor: spec.bgColor,
    color: spec.color,
    icon: venueCategoryIcons[spec.iconName],
    label: spec.label,
  };
}
